import { animate, state, style, transition, trigger } from '@angular/animations';
import { ContentObserver } from '@angular/cdk/observers';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, defer } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { ElementSafeObserver } from './element-safe-observer';
import { PanelSize, SimplePanelButton, SimplePanelOptions } from './models';
import { SimplePanelService } from './simple-panel.service';
import { ScrollService } from '../scroll.service';
import { SubsManager } from '../subs-manager';
import { TemplateUtilService } from '../template-util.service';

@Component({
  selector: 'tcc-simplePanel',
  templateUrl: './simple-panel.component.html',
  styleUrls: ['simple-panel.component.scss'],
  animations: [
    trigger('routeAnimation', [
      state('*',
        style({
          opacity: 1
        })
      ),
      transition(':enter', [
        style({
          opacity: 0,
        }),
        animate('300ms ease-in')
      ])
    ])
  ]
})
export class SimplePanelComponent implements OnDestroy, OnInit, AfterViewInit {
  /** collection of SimplePanelButton whose names can be passed to buttons. */
  static readonly standardButtons: { [key: string]: SimplePanelButton } = {
    close: { iconClass: 'fa fa-times', action: 'close' }
  };

  /** (Experimental as of 11.0.2) If true then this panel is part of the active route.  */
  private get isActiveRoute() {
    const urlTree = this.router.createUrlTree(["."], { relativeTo: this.route });
    return (this.router.isActive(urlTree, true));
  }
  private optionsSubject = new BehaviorSubject<SimplePanelOptions | undefined>(undefined);
  private panelFooterObserver = new ElementSafeObserver(this.obs);
  private panelHeaderObserver = new ElementSafeObserver(this.obs);
  private panelSubheaderObserver = new ElementSafeObserver(this.obs);
  private subsManager = new SubsManager();

  /** if true, the panel will be scrolled to afterviewInit */
  @Input() autoScroll = true;

  /** Input for buttons that appear in the control header - either a string or array of strings and buttons */
  @Input() set buttons(value: (string | (string | SimplePanelButton)[])) {
    if (typeof (value) === 'string') {
      this.parsedButtons = value.split(',').map(x => SimplePanelComponent.standardButtons[x.trim()] || { action: x.trim() });
    }
    else {
      this.parsedButtons = (value || []).map(b => {
        return (typeof (b) === 'string') ? SimplePanelComponent.standardButtons[b] || { action: b } : b;
      });
    }
  }
  /** emitted when a header button is clicked, the event data is the name of the action */
  @Output() buttonClick = new EventEmitter<string>();

  /** root element of the control's template */
  @ViewChild('container', { static: true })
  container?: ElementRef;

  /** options for the simple panel */
  @Input()
  set options(value: SimplePanelOptions | undefined) {
    if (this.optionsSubject.value !== value) {
      this.optionsSubject.next(value);
    }
  };
  get options() { return this.optionsSubject.value; };

  /** reference to the body element. */
  @ViewChild('panelBody')
  panelBody?: ElementRef;

  /** reference to the footer element. */
  @ViewChild('panelFooter')
  get panelFooter() { return this.panelFooterObserver.element; }
  set panelFooter(value: ElementRef | undefined) { this.panelFooterObserver.element = value; }

  panelFooterIsEmpty$ = this.createElemIsEmpty$(this.panelFooterObserver);

  /** reference to the header element. */
  @ViewChild('panelHeader')
  get panelHeader() { return this.panelHeaderObserver.element; }
  set panelHeader(value: ElementRef | undefined) { this.panelHeaderObserver.element = value; }

  panelHeaderIsEmpty$ = this.createElemIsEmpty$(this.panelHeaderObserver);

  /** reference to the subheader element. */
  @ViewChild('panelSubheaderContainer')
  get panelSubheaderContainer() { return this.panelSubheaderObserver.element; }
  set panelSubheaderContainer(value: ElementRef | undefined) { this.panelSubheaderObserver.element = value; }

  panelSubheaderContainerIsEmpty$ = this.createElemIsEmpty$(this.panelSubheaderObserver);

  /** buttons after they've been parsed */
  parsedButtons: SimplePanelButton[] = [];

  /** backing var for size property */
  private _size: PanelSize | undefined;

  /** the size of the panel either small, large or full. */
  @Input() get size() { return this._size; }
  set size(value: PanelSize | undefined) {
    const newSize = value || 'large';
    if (newSize !== this._size) {
      this._size = newSize;
      this.sizeChange.emit(this._size);
    }
  }
  /** emits when the size is changed */
  @Output() sizeChange = new EventEmitter<PanelSize>();

   /** css classes that should be displayed on the panel given the current state */
  panelOuterCssClasses$ = combineLatest([
    this.optionsSubject,
    defer(() => this.sizeChange.pipe(startWith(this._size), map(x => x ?? 'large'))),
    this.simplePanelSvc.mainMenuChange$.pipe(startWith('none'))
  ]).pipe(
    debounceTime(1),
    map(([options, size]) => this.getOuterCssClasses(options, size)),
    shareReplay(1)
  );

  constructor(
    private route: ActivatedRoute, private router: Router, private scrollSvc: ScrollService,
    private simplePanelSvc: SimplePanelService, public templateUtil: TemplateUtilService,
    private obs: ContentObserver) {

  }

  ngOnInit() {
    this.simplePanelSvc.ensureCompatibleHostCss(this.container);

    this.subsManager.addSub = this.simplePanelSvc.onResize.subscribe(() => this.simplePanelSvc.ensureCompatibleHostCss(this.container));
    this.subsManager.addSub = this.router.events.pipe(
      // scroll this into view if autoScroll is enabled and a navigation event occurs where this is no the last child.
      filter(evt => this.autoScroll && evt instanceof NavigationEnd && this.isActiveRoute),
      tap(() => this.scrollIntoView())
    ).subscribe();
    this.subsManager.addSub = this.optionsSubject.pipe(
      filter(x => !!x?.handleAnchorScroll),
      switchMap(() => this.router.events),
      filter((x: any): x is { anchor: string } => !!x?.anchor),
      tap(x => this.scrollToAnchorOrId(x.anchor))
    ).subscribe()
  }

  ngAfterViewInit() {
    if (this.autoScroll && this.isActiveRoute) {
      this.scrollIntoView();
    }
    if (this.options?.handleAnchorScroll && this.route.snapshot.fragment!) {
      this.scrollToAnchorOrId(this.route.snapshot.fragment);
    }
  }

  ngOnDestroy() {
    this.subsManager.onDestroy();
  }

  /** gets text for a button */
  getButtonText(button: SimplePanelButton) {
    return (!button.text && !button.iconClass) ? button.action : button.text;
  }


  /** emits a buttonClick Component Output */
  emitButtonClick(button: SimplePanelButton) {
    this.buttonClick.emit(button.action);
  }

  /** Makes this window active on the screen. */
  scrollIntoView() {
    setTimeout(() => {
      if (this.container) {
        this.scrollSvc.scrollIntoView(this.container);
      }
    });
  }

  /** scrolls to either a name="nameOrId" or any element with the id of nameOrId */
  scrollToAnchorOrId(nameOrId: string) {
    const elem = this.panelBody?.nativeElement as HTMLElement;
    const scrollToElem = elem?.querySelector(`a[name=${nameOrId}]`) ?? elem?.querySelector(`#${nameOrId}`);
    const scrollToElemRef = new ElementRef(scrollToElem);
    this.scrollSvc.scrollIntoView(scrollToElemRef);
  }

  /** scrolls to the top of the panel */
  scrollToTop() {
    if (this.panelBody) {
      this.scrollSvc.scrollToDirection(this.panelBody, 'top');
    }
  }

  /** scrolls to the bottom of the panel */
  scrollToBottom() {
    if (this.panelBody) {
      this.scrollSvc.scrollToDirection(this.panelBody, 'bottom');
    }
  }

  /** Creates an observable that will fire any time an elements empty status changes. */
  private createElemIsEmpty$(elemObserver: ElementSafeObserver) {
    return elemObserver.elem$.pipe(map(x => this.templateUtil.elemIsEmpty(x)), distinctUntilChanged(), shareReplay(1));
  }

  /** calculates classes for the outer element based on the state of the component. */
  private getOuterCssClasses(options: SimplePanelOptions | undefined, size: PanelSize) {
    const cssClasses: string[] = [`panelOuter-${size}`];
    ifTruthyPush(options?.respondsToMainMenu && this.simplePanelSvc.mainMenu, 'main-menu-responsive');
    ifTruthyPush(options?.respondsToMainMenu && this.simplePanelSvc.mainMenuIsExpanded, 'menu-expanded');
    ifTruthyPush(options?.isFloating, 'panelOuter-floating');
    return cssClasses;

    function ifTruthyPush(addClass: any, cssClass: string) {
      if (addClass) {
        cssClasses.push(cssClass);
      }
    }
  }

}

